import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CONTACT } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Our team will be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "bg-brand-surface border-brand-line text-brand-text placeholder:text-brand-muted h-12 rounded-none focus-visible:ring-brand focus-visible:border-brand transition-colors";

  return (
    <section id="contact" className="relative py-24 md:py-40 border-t border-brand-line" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="mb-16 max-w-3xl">
          <p className="overline mb-6">08 — Start a conversation</p>
          <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight text-brand-text leading-[1.02]">
            Let&apos;s build your people advantage.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-24">
          {/* Form */}
          <Reveal>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-soft">Full name *</label>
                  <Input data-testid="contact-name" name="name" value={form.name} onChange={onChange} placeholder="Jane Doe" className={`mt-3 ${field}`} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-soft">Email *</label>
                  <Input data-testid="contact-email" name="email" type="email" value={form.email} onChange={onChange} placeholder="jane@company.com" className={`mt-3 ${field}`} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-soft">Phone</label>
                  <Input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} placeholder="+234 ..." className={`mt-3 ${field}`} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-soft">Subject</label>
                  <Input data-testid="contact-subject" name="subject" value={form.subject} onChange={onChange} placeholder="Training enquiry" className={`mt-3 ${field}`} />
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-soft">Message *</label>
                <Textarea data-testid="contact-message" name="message" value={form.message} onChange={onChange} placeholder="Tell us about your organization and what you're looking for..." rows={5} className={`mt-3 ${field} h-auto py-3`} />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-brand text-brand-base font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-brand-hover disabled:opacity-60 transition-colors duration-300"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                {loading ? "Sending" : "Send message"}
              </button>
            </form>
          </Reveal>

          {/* Offices */}
          <Reveal delay={0.15}>
            <div className="space-y-10">
              {CONTACT.offices.map((o) => (
                <div key={o.city} className="border-t border-brand-line pt-6" data-testid={`office-${o.city.toLowerCase()}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin size={16} className="text-brand" />
                    <h3 className="font-display text-2xl text-brand-text">{o.city}</h3>
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-brand-muted border border-brand-line px-2 py-1">{o.tag}</span>
                  </div>
                  <div className="text-brand-soft font-light leading-relaxed">
                    {o.lines.map((l, i) => <p key={i}>{l}</p>)}
                  </div>
                </div>
              ))}

              <div className="border-t border-brand-line pt-6 space-y-4">
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-brand-text hover:text-brand transition-colors group">
                  <Mail size={16} className="text-brand" />
                  <span className="font-light">{CONTACT.email}</span>
                </a>
                <div className="flex items-start gap-3 text-brand-soft">
                  <Phone size={16} className="text-brand mt-1" />
                  <div className="font-light space-y-1">
                    {CONTACT.phones.map((p) => <p key={p}>{p}</p>)}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
